import type { FC } from 'react';
import { useState, useCallback, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { BatchActionProps } from '../types';
import MobileBatchActionWrapper from '../MobileBatchActionWrapper';
import DesktopBatchActionWrapper from '../DesktopBatchActionWrapper';
import ButtonFieldLayout from '@wix/da-submit-shared/pkg/layouts/common/ButtonFieldLayout';
import MainToggle from '@wix/da-ds/pkg/formControls/dsPresets/MainToggle';
import SelectField from '@wix/da-ds/pkg/Dropdown/select/SelectField';
import { DropdownOption } from '@wix/da-ds/pkg/Dropdown/types';
import s from './DisplayOptionsBatchEdit.scss';

export interface Props extends BatchActionProps {
  batchEditDisplayOptions: (displayResolution: number, addWatermark: boolean) => void;
}

const DisplayOptionsBatchEdit: FC<Props> = ({ batchEditDisplayOptions, selectionArea }) => {
  const { t } = useTranslation();

  const isMobile = useContext(MobileContext);
  const [watermarkEnabled, setWatermarkEnabled] = useState(false);
  const [selectedDisplayResolution, setSelectedDisplayResolution] = useState(0);

  const handleSave = useCallback(() => {
    batchEditDisplayOptions(selectedDisplayResolution, watermarkEnabled);
  }, [watermarkEnabled, batchEditDisplayOptions, selectedDisplayResolution]);

  const id = 'watermark';

  const items = useMemo<DropdownOption[]>(() => {
    const displayOptions = [
      { width: 'original', key: 0 },
      { width: '400px', key: 1 },
      { width: '600px', key: 2 },
      { width: '800px', key: 3 },
      { width: '900px', key: 4 },
      { width: '1024px', key: 5 },
      { width: '1280px', key: 6 },
      { width: '1600px', key: 7 },
      { width: '1920px', key: 8 },
    ];

    return displayOptions.map(({ width, key }) => {
      return {
        label:
          key === 0
            ? t('studio.batchActions.form.display_options.original')
            : t('studio.batchActions.form.display_options.width', { width }),
        value: key,
      };
    });
  }, [t]);

  const handleWidthSelection = useCallback(
    (value: string) => {
      setSelectedDisplayResolution(Number(value));
      // disable watermark if "original" is selected
      if (value === '0') {
        setWatermarkEnabled(false);
      }
    },
    [setWatermarkEnabled]
  );

  const handleWatermarkToggle = useCallback(() => {
    setWatermarkEnabled(!watermarkEnabled);
    // set to 1280px if watermark is enabled while "original" is selected
    if (!watermarkEnabled && selectedDisplayResolution === 0) {
      setSelectedDisplayResolution(6);
    }
  }, [watermarkEnabled, selectedDisplayResolution]);

  const form = (
    <div>
      <SelectField
        className={s['operation-field']}
        id={'image-size'}
        items={items}
        onSelect={handleWidthSelection}
        value={selectedDisplayResolution}
        label={t('studio.batch.watermark.label')}
        isLabelHidden
        withBlankOption={false}
      />
      <ButtonFieldLayout
        label={t('studio.batchActions.form.edit_watermark.add')}
        labelProps={{ id: `${id}-label`, htmlFor: id }}
        subLabel={
          <div className={s['subtitle']}>{t('studio.batchActions.subtitle.edit_watermark')}</div>
        }
      >
        <MainToggle
          variant="main"
          id={id}
          size={isMobile ? 'large' : 'small'}
          value={watermarkEnabled}
          onChange={handleWatermarkToggle}
          aria-labelledby={`${id}-label`}
          className={s['toggle']}
        />
      </ButtonFieldLayout>
    </div>
  );

  if (isMobile) {
    return (
      <MobileBatchActionWrapper
        heading={t('studio.batchActions.subtitle.edit_display_options')}
        onSave={handleSave}
        restrictSave="non_core_multiple"
      >
        {form}
      </MobileBatchActionWrapper>
    );
  }

  return (
    <DesktopBatchActionWrapper
      heading={t('studio.batchActions.subtitle.edit_display_options')}
      selectionArea={selectionArea}
      onSave={handleSave}
      restrictSave="non_core_multiple"
    >
      {form}
    </DesktopBatchActionWrapper>
  );
};
DisplayOptionsBatchEdit.displayName = 'DisplayOptionsBatchEdit';

export default DisplayOptionsBatchEdit;
